import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import Lottie from 'lottie-react';
// Api
import { GET_MY_PAYMENT_BY_EXTERNAL_ID } from 'api/payments/queries';
// Ui3
import Button from 'ui3/Button/Button';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Modal from 'ui3/Modal/Modal';
import Text from 'ui3/Text/Text';
// Animations
import bitcoinAnimation from 'public/bitcoin-animation.json';
// Styles
import styles from './BitcoinPaymentDisplay.module.scss';

interface BitcoinPaymentDisplayProps {
  invoiceUrl: string;
  amount: number;
  tipId: string;
  orderType?: 'user_tip' | 'stream_tip';
  onPaymentSent: () => void;
  onConfettiStart?: () => void;
}

const BitcoinPaymentDisplay: React.FC<BitcoinPaymentDisplayProps> = ({
  invoiceUrl,
  amount,
  tipId,
  orderType,
  onPaymentSent,
  onConfettiStart,
}) => {
  const [isTransactionReceived, setIsTransactionReceived] = useState<boolean>(
    false
  );
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'successful'>(
    'pending'
  );
  const [showProcessModal, setShowProcessModal] = useState<boolean>(false);

  // Polling control: poll every 30s, max 10 attempts
  const MAX_POLL_ATTEMPTS = 10;
  const [pollCount, setPollCount] = useState<number>(0);

  useQuery(GET_MY_PAYMENT_BY_EXTERNAL_ID, {
    variables: {
      input: {
        externalOrderId: tipId,
        orderType: orderType,
      },
    },
    skip: !tipId || isTransactionReceived || pollCount >= MAX_POLL_ATTEMPTS,
    pollInterval:
      isTransactionReceived || pollCount >= MAX_POLL_ATTEMPTS ? 0 : 30000,
    errorPolicy: 'all',
    onCompleted: (data) => {
      setPollCount((prev) => prev + 1);

      const payment = data?.getMyPaymentByExternalId;

      if (payment) {
        const hasTransactionId = payment.transactionId;

        if (hasTransactionId && !isTransactionReceived) {
          setIsTransactionReceived(true);
          setPaymentStatus('pending');
          onPaymentSent?.();
        }
      }
    },
  });

  const handleConfirmPayment = () => {
    setShowProcessModal(true);
  };

  const handleCloseProcessModal = () => {
    setShowProcessModal(false);
    if (onConfettiStart) {
      onConfettiStart();
    }
  };

  return (
    <>
      {!showProcessModal && (
        <div className={styles.container}>
          <div className={styles.header}>
            <h3 className={styles.title}>Bitcoin Payment</h3>
            <div className={styles.amountDisplay}>
              <MillionsIcon name="bitcoin" size={24} />
              <span className={styles.amount}>{amount} BTC</span>
            </div>
          </div>

          {paymentStatus !== 'successful' && (
            <div className={styles.paymentSection}>
              <iframe
                src={invoiceUrl}
                className={styles.paymentFrame}
                title="Bitcoin Payment"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </div>
          )}

          <div className={styles.warningSection}>
            <div className={styles.warningIcon}>
              <MillionsIcon name="alertTriangle" size={20} />
            </div>
            <div className={styles.warningContent}>
              <Text variant="body1Regular16" color="lights-high">
                Please transfer to this address using your preferred crypto
                exchange. Soon as its received you&apos;ll get confirmation and
                a tip graphic to share the love.
              </Text>
            </div>
          </div>

          <div className={styles.footer}>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirmPayment}
              className={styles.closeButton}
            >
              Confirm Payment
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={showProcessModal}
        onClose={handleCloseProcessModal}
        size="large"
        showCloseIcon={true}
      >
        <div className={styles.processModal}>
          <div className={styles.iconWrapper}>
            <Lottie
              animationData={bitcoinAnimation}
              loop={true}
              autoplay={true}
              style={{
                width: 200,
                height: 150,
              }}
            />
          </div>

          <Text variant="h3" textAlign="center" className={styles.modalTitle}>
            Your Tip is in Process!
          </Text>

          <Text
            variant="body1Regular16"
            textAlign="center"
            color="light-white"
            className={styles.modalDescription}
          >
            We&apos;ll notify you soon as its received and send you and the
            recipient a cool graphic to share.
          </Text>

          <Button
            type="button"
            variant="primary"
            onClick={handleCloseProcessModal}
            className={styles.modalButton}
          >
            Got it
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default BitcoinPaymentDisplay;
